import React from 'react'
import {
    Grid,
    FormControl,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    Box,
} from '@mui/material'
import { FormInputLabel } from '@/shared/ui/input'

interface RecurrenceProps {
    kedo: any
    recurrent: { name: string; value: string | null }[]
    curPeriod: string
    handleRecurChange: (e: any) => void
    shortDays: { name: string; value: string }[]
    days: string[]
    setDays: React.Dispatch<React.SetStateAction<string[]>>
    changeDay: (e: any) => void
    mode: string
    changeMonthMode: (e: any) => void
    byMonthDay: string
    setByMonthDay: (val: any) => void
    bySetPos: string
    setBySetPos: (val: any) => void
    shortMonths: { name: string; value: string }[]
    byMonth: string
    setByMonth: (val: any) => void
    startDate: Date | null
    intervaling: number
    setIntervaling: (val: number) => void
    isValidNumericInput: (value: string, e: any) => boolean
    translateDays: (period: string | null) => string
}

const RecurrenceForm: React.FC<RecurrenceProps> = ({
    kedo,
    recurrent,
    curPeriod,
    handleRecurChange,
    shortDays,
    days,
    setDays,
    changeDay,
    mode,
    changeMonthMode,
    byMonthDay,
    setByMonthDay,
    bySetPos,
    setBySetPos,
    shortMonths,
    byMonth,
    setByMonth,
    startDate,
    intervaling,
    setIntervaling,
    isValidNumericInput,
    translateDays,
}) => {
    // Handler for daily/weekly toggle buttons
    const handleDaysChange = (
        _: React.MouseEvent<HTMLElement>,
        newDays: string[]
    ) => {
        setDays(newDays)
    }

    // Handler for currentPeriod radio buttons
    const handleCurrentPeriodChange = (
        _: React.MouseEvent<HTMLElement>,
        newPeriod: string
    ) => {
        handleRecurChange(newPeriod)
    }

    return (
        <Grid container>
            <FormControl fullWidth margin="normal">
                <Grid container>
                    <Grid size={{ xs: 12, sm: 2 }}>
                        <FormInputLabel>{kedo.t('Repeat')}</FormInputLabel>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 10 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                mt: 1,
                            }}
                        >
                            {/* Recurrent period toggle */}
                            <ToggleButtonGroup
                                value={curPeriod}
                                exclusive
                                onChange={handleCurrentPeriodChange}
                                sx={{
                                    mb: 1,
                                    width: '100%',
                                    '& .MuiToggleButton-root': {
                                        flex: 1,
                                    },
                                }}
                                size="small"
                            >
                        {recurrent.map((recur, idx) => (
                            <ToggleButton
                                key={idx}
                                value={recur.value ?? ''}
                                color={
                                    curPeriod === (recur.value || null)
                                        ? 'secondary'
                                        : 'primary'
                                }
                            >
                                {recur.name}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    {/* Daily/Weekly days selection */}
                            {['daily', 'weekly'].includes(curPeriod || '') && (
                                <ToggleButtonGroup
                                    value={days}
                                    onChange={handleDaysChange}
                                    sx={{
                                        mb: 1,
                                        width: '100%',
                                        '& .MuiToggleButton-root': {
                                            flex: 1,
                                        },
                                    }}
                                    size="small"
                                    exclusive={false}
                                    disabled={!curPeriod}
                                >
                            {shortDays.map((day, idx) => (
                                <ToggleButton
                                    key={idx}
                                    value={day.value}
                                    color={
                                        days.includes(day.value)
                                            ? 'secondary'
                                            : 'primary'
                                    }
                                >
                                    {day.name}
                                </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            )}

                            {/* Monthly options */}
                            {['monthly'].includes(curPeriod || '') && (
                        <Box>
                            {/* By month day */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1,
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mode === 'BYMONTHDAY'}
                                            onChange={changeMonthMode}
                                            name="BYMONTHDAY"
                                            size="small"
                                        />
                                    }
                                    label={kedo.t('On day')}
                                    sx={{ mr: 2 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 80 }}>
                                    <Select
                                        value={byMonthDay || '1'}
                                        disabled={mode !== 'BYMONTHDAY'}
                                        size="small"
                                        onChange={(e) => {
                                            setByMonthDay(e.target.value)
                                            if (
                                                curPeriod === 'monthly' &&
                                                mode === 'BYMONTHDAY' &&
                                                startDate
                                            ) {
                                                const newDay = parseInt(
                                                    e.target.value
                                                )
                                                const newStart = new Date(
                                                    startDate
                                                )
                                                newStart.setDate(newDay)
                                                // Assuming there's a setter for startDate
                                            }
                                        }}
                                    >
                                        {Array.from(
                                            { length: 31 },
                                            (_, i) => i + 1
                                        ).map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* By set position */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1,
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mode === 'BYSETPOS'}
                                            onChange={changeMonthMode}
                                            name="BYSETPOS"
                                        />
                                    }
                                    label={kedo.t('On the')}
                                    sx={{ mr: 2 }}
                                />
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 80, mr: 1 }}
                                >
                                    <Select
                                        value={bySetPos || '1'}
                                        disabled={mode !== 'BYSETPOS'}
                                        size="small"
                                        onChange={(e) =>
                                            setBySetPos(e.target.value)
                                        }
                                    >
                                        <MenuItem value="1">
                                            {kedo.t('First')}
                                        </MenuItem>
                                        <MenuItem value="2">
                                            {kedo.t('Second')}
                                        </MenuItem>
                                        <MenuItem value="3">
                                            {kedo.t('Third')}
                                        </MenuItem>
                                        <MenuItem value="4">
                                            {kedo.t('Fourth')}
                                        </MenuItem>
                                        <MenuItem value="-1">
                                            {kedo.t('Last')}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 120 }}
                                >
                                    <Select
                                        value={
                                            days.length
                                                ? days.toString()
                                                : 'Monday'
                                        }
                                        disabled={mode !== 'BYSETPOS'}
                                        size="small"
                                        onChange={changeDay}
                                    >
                                        <MenuItem value="Monday">
                                            {kedo.t('days_full.Monday')}
                                        </MenuItem>
                                        <MenuItem value="Tuesday">
                                            {kedo.t('days_full.Tuesday')}
                                        </MenuItem>
                                        <MenuItem value="Wednesday">
                                            {kedo.t('days_full.Wednesday')}
                                        </MenuItem>
                                        <MenuItem value="Thursday">
                                            {kedo.t('days_full.Thursday')}
                                        </MenuItem>
                                        <MenuItem value="Friday">
                                            {kedo.t('days_full.Friday')}
                                        </MenuItem>
                                        <MenuItem value="Saturday">
                                            {kedo.t('days_full.Saturday')}
                                        </MenuItem>
                                        <MenuItem value="Sunday">
                                            {kedo.t('days_full.Sunday')}
                                        </MenuItem>
                                        <MenuItem value="Monday,Tuesday,Wednesday,Thursday,Friday">
                                            {kedo.t('Weekday')}
                                        </MenuItem>
                                        <MenuItem value="Saturday,Sunday">
                                            {kedo.t('Weekend day')}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    )}

                    {/* Yearly options */}
                    {['yearly'].includes(curPeriod || '') && (
                        <Box>
                            {/* By month day */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1,
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mode === 'BYMONTH'}
                                            onChange={changeMonthMode}
                                            name="BYMONTH"
                                        />
                                    }
                                    label={kedo.t('On day')}
                                    sx={{ mr: 2 }}
                                />
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 80, mr: 1 }}
                                >
                                    <Select
                                        value={byMonthDay || '1'}
                                        disabled={mode !== 'BYMONTH'}
                                        size="small"
                                        onChange={(e) =>
                                            setByMonthDay(e.target.value)
                                        }
                                    >
                                        {Array.from(
                                            { length: 31 },
                                            (_, i) => i + 1
                                        ).map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 100 }}
                                >
                                    <Select
                                        value={byMonth || '1'}
                                        disabled={mode !== 'BYMONTH'}
                                        size="small"
                                        onChange={(e) =>
                                            setByMonth(e.target.value)
                                        }
                                    >
                                        {shortMonths.map((month) => (
                                            <MenuItem
                                                key={month.value}
                                                value={month.value}
                                            >
                                                {month.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* By set position */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1,
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mode === 'BYSETPOS'}
                                            onChange={changeMonthMode}
                                            name="BYSETPOS"
                                        />
                                    }
                                    label={kedo.t('On the')}
                                    sx={{ mr: 2 }}
                                />
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 80, mr: 1 }}
                                >
                                    <Select
                                        value={bySetPos || '1'}
                                        disabled={mode !== 'BYSETPOS'}
                                        size="small"
                                        onChange={(e) =>
                                            setBySetPos(e.target.value)
                                        }
                                    >
                                        <MenuItem value="1">
                                            {kedo.t('First')}
                                        </MenuItem>
                                        <MenuItem value="2">
                                            {kedo.t('Second')}
                                        </MenuItem>
                                        <MenuItem value="3">
                                            {kedo.t('Third')}
                                        </MenuItem>
                                        <MenuItem value="4">
                                            {kedo.t('Fourth')}
                                        </MenuItem>
                                        <MenuItem value="-1">
                                            {kedo.t('Last')}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 120, mr: 1 }}
                                >
                                    <Select
                                        value={
                                            days.length
                                                ? days.toString()
                                                : 'Monday'
                                        }
                                        disabled={mode !== 'BYSETPOS'}
                                        size="small"
                                        onChange={changeDay}
                                    >
                                        <MenuItem value="Monday">
                                            {kedo.t('days_full.Monday')}
                                        </MenuItem>
                                        <MenuItem value="Tuesday">
                                            {kedo.t('days_full.Tuesday')}
                                        </MenuItem>
                                        <MenuItem value="Wednesday">
                                            {kedo.t('days_full.Wednesday')}
                                        </MenuItem>
                                        <MenuItem value="Thursday">
                                            {kedo.t('days_full.Thursday')}
                                        </MenuItem>
                                        <MenuItem value="Friday">
                                            {kedo.t('days_full.Friday')}
                                        </MenuItem>
                                        <MenuItem value="Saturday">
                                            {kedo.t('days_full.Saturday')}
                                        </MenuItem>
                                        <MenuItem value="Sunday">
                                            {kedo.t('days_full.Sunday')}
                                        </MenuItem>
                                        <MenuItem value="Monday,Tuesday,Wednesday,Thursday,Friday">
                                            {kedo.t('Weekday')}
                                        </MenuItem>
                                        <MenuItem value="Saturday,Sunday">
                                            {kedo.t('Weekend day')}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 100 }}
                                >
                                    <Select
                                        value={byMonth || '1'}
                                        disabled={mode !== 'BYSETPOS'}
                                        size="small"
                                        onChange={(e) =>
                                            setByMonth(e.target.value)
                                        }
                                    >
                                        {shortMonths.map((month) => (
                                            <MenuItem
                                                key={month.value}
                                                value={month.value}
                                            >
                                                {month.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    )}

                    {/* Repeat interval */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <FormInputLabel sx={{ mr: 1 }}>
                            {kedo.t('Repeat every')}
                        </FormInputLabel>
                        <TextField
                            size="small"
                            type="number"
                            value={intervaling}
                            onChange={(e) => {
                                const value = e.target.value
                                if (isValidNumericInput(value, e)) {
                                    if (value === '') setIntervaling(0)
                                    else setIntervaling(parseInt(value))
                                }
                            }}
                            sx={{ width: 60, mr: 1 }}
                        />
                        <FormInputLabel>
                            {kedo.t(translateDays(curPeriod)).toLowerCase()}
                        </FormInputLabel>
                    </Box>
                        </Box>
                    </Grid>
                </Grid>
            </FormControl>
        </Grid>
    )
}

export default RecurrenceForm
